import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as api from "../../../utils/api/deliveryDocumentation";
import AdminSummary from "./AdminSummary";
import AdminOrderDetails from "./AdminOrderDetails";
import AuditTimeline from "./AuditTimeline";
import DeletedOrders from "./DeletedOrders";
import DocumentationFilters from "./DocumentationFilters";
import DocumentationHierarchy from "./DocumentationHierarchy";
import DisputeWorkspace from "./disputes/DisputeWorkspace";
import DisputeSettings from "./disputes/DisputeSettings";
import { Button } from "../../ui";
import { AdminPagination } from "./AdminUi";
import { usePageSection } from "../../../navigation/pageSections";
import "./adminDocumentation.css";

function Fold({ title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={`admin-fold ${open ? "open" : ""}`}>
      <button
        className="admin-fold-head"
        type="button"
        onClick={() => setOpen((value) => !value)}
      >
        <div>
          <b>{title}</b>
          <span>{subtitle}</span>
        </div>
        <span className="admin-fold-toggle">{open ? "تصغير" : "تكبير"}</span>
      </button>
      <div className="admin-fold-body">{children}</div>
    </section>
  );
}

export default function DocumentationAdmin() {
  // The four main areas are opened from the sidebar submenu (`?tab=`).
  const [activeTab] = usePageSection("delivery-documentation-admin");
  const [params, setParams] = useSearchParams();
  const initialSection = params.get("section");
  const [logsSection, setLogsSection] = useState(initialSection === "deleted" ? "deleted" : "audit");
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [filters, setFilters] = useState({ sort: "createdAt_desc" });
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [summaryState, setSummaryState] = useState({
    loading: true,
    error: "",
    summary: {},
  });
  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedFilters(filters), 350);
    return () => window.clearTimeout(timer);
  }, [filters]);
  const loadSummary = useCallback(async () => {
    setSummaryState((value) => ({ ...value, loading: true, error: "" }));
    try {
      const data = await api.getAdminDocumentationSummary(debouncedFilters);
      setSummaryState({
        loading: false,
        error: "",
        summary: data.summary || {},
      });
    } catch (error) {
      setSummaryState((value) => ({
        ...value,
        loading: false,
        error: error.message,
      }));
    }
  }, [debouncedFilters]);
  useEffect(() => {
    if (activeTab === "documentation") loadSummary();
  }, [activeTab, loadSummary]);
  const [auditState, setAuditState] = useState({
    loading: true,
    error: "",
    items: [],
    pagination: { page: 1 },
  });
  const loadAudit = useCallback(
    async (page = 1) => {
      setAuditState((value) => ({ ...value, loading: true, error: "" }));
      try {
        const data = await api.getAdminAuditLog({
          ...debouncedFilters,
          page,
          limit: 30,
        });
        setAuditState({
          loading: false,
          error: "",
          items: data.items || [],
          pagination: data.pagination || {},
        });
      } catch (error) {
        setAuditState((value) => ({
          ...value,
          loading: false,
          error: error.message,
        }));
      }
    },
    [debouncedFilters],
  );
  useEffect(() => {
    if (activeTab === "logs" && logsSection === "audit") loadAudit(1);
  }, [activeTab, logsSection, loadAudit]);
  function switchLogsSection(section) {
    setLogsSection(section);
    const next = new URLSearchParams(params);
    next.set("tab", "logs");
    next.set("section", section);
    setParams(next, { replace: true });
  }
  return (
    <main className="documentation-admin" dir="rtl">
      <section className="admin-hero">
        <div>
          <h1>مساحة إدارة توثيق التوصيل</h1>
          <p>أربع مساحات رئيسية تحفظ كل وظائف التوثيق والاعتراضات والسجلات في نفس route.</p>
        </div>
      </section>
      {activeTab === "documentation" && (
        <>
          <Fold title="ملخص" subtitle="المؤشرات التشغيلية محسوبة من الخادم حسب الفلاتر النشطة">
            <AdminSummary {...summaryState} onRetry={loadSummary} />
          </Fold>
          <Fold title="فلتر الجميع" subtitle="فلتر واحد ينعكس على الملخص وسجل الطلبات">
            <DocumentationFilters
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters({ sort: "createdAt_desc" })}
            />
          </Fold>
        <Fold
          title="سجل الطلبات والتوثيق"
          subtitle="فلترة الطلبات ثم الانتقال داخل نفس المساحة حتى الوصول للطلب"
        >
          <DocumentationHierarchy
            filters={debouncedFilters}
            onOpenOrder={setSelectedOrderId}
          />
        </Fold>
        </>
      )}
      {activeTab === "disputes" && (
        <>
          <Fold
            title="طلبات الاعتراض"
            subtitle="ملخص الاعتراضات، الفلاتر، والجاهزة/غير الجاهزة/المعترض عنها من الخادم"
          >
            <DisputeWorkspace />
          </Fold>
        </>
      )}
      {activeTab === "dispute-settings" && (
        <Fold title="إعدادات الاعتراض" subtitle="إعداد واحد قابل لإعادة الاستخدام لكل تطبيق توصيل">
          <DisputeSettings />
        </Fold>
      )}
      {activeTab === "logs" && (
        <>
          <div className="admin-subtabs">
            <Button className={logsSection === "audit" ? "on" : ""} variant={logsSection === "audit" ? "primary" : "neutral"} appearance="soft" type="button" onClick={() => switchLogsSection("audit")}>سجل التغيرات</Button>
            <Button className={logsSection === "deleted" ? "on" : ""} variant={logsSection === "deleted" ? "primary" : "neutral"} appearance="soft" type="button" onClick={() => switchLogsSection("deleted")}>المحذوفات</Button>
          </div>
          {logsSection === "audit" && (
            <Fold title="سجل التغيرات" subtitle="كل نشاط توثيق التوصيل من نظام السجلات الحالي">
              <AuditTimeline {...auditState} onRetry={() => loadAudit(auditState.pagination.page || 1)} />
              <AdminPagination
                page={auditState.pagination.page}
                totalPages={auditState.pagination.totalPages}
                hasPreviousPage={auditState.pagination.hasPreviousPage}
                hasNextPage={auditState.pagination.hasNextPage}
                onPrevious={() => loadAudit(auditState.pagination.page - 1)}
                onNext={() => loadAudit(auditState.pagination.page + 1)}
              />
            </Fold>
          )}
          {logsSection === "deleted" && (
            <Fold title="المحذوفات" subtitle="الطلبات المحذوفة">
              <DeletedOrders filters={debouncedFilters} onOpenOrder={setSelectedOrderId} />
            </Fold>
          )}
        </>
      )}
      <AdminOrderDetails
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId("")}
      />
    </main>
  );
}
